<script setup>
/**
 * NotificationBell - Bell icon with badge and dropdown
 * Desktop: Shows dropdown on hover
 * Mobile: Can emit event for full-screen panel
 */
import { computed } from 'vue';
import { useNotificationsStore } from '@/stores/notificationsStore';
import NotificationDropdown from './NotificationDropdown.vue';

const props = defineProps({
  // For mobile: emit click instead of showing dropdown
  mobileMode: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['mobile-click']);

const notificationsStore = useNotificationsStore();
const unreadCount = computed(() => notificationsStore.unreadCount);

const showBadge = computed(() => unreadCount.value > 0);

const handleClick = () => {
  if (props.mobileMode) {
    emit('mobile-click');
  }
};
</script>

<template>
  <!-- Mobile mode: just icon -->
  <div
    v-if="mobileMode"
    class="navbar-icon-wrapper"
    @click="handleClick"
  >
    <span class="icon navbar-icon" :class="showBadge ? 'has-text-danger' : 'has-text-warning'">
      <font-awesome-icon
        :icon="['fad', 'bell']"
        :active-icon="['fad', 'bells']"
        :active="showBadge"
      />
    </span>
    <span v-if="showBadge" class="notification-dot"></span>
  </div>

  <!-- Desktop mode: hoverable dropdown -->
  <div v-else class="dropdown is-hoverable is-right notification-bell-dropdown">
    <div class="dropdown-trigger">
      <div class="navbar-icon-wrapper" aria-haspopup="true">
        <span class="icon navbar-icon" :class="showBadge ? 'has-text-danger' : 'has-text-warning'">
          <font-awesome-icon
            :icon="['fad', 'bell']"
            :active-icon="['fad', 'bells']"
            :active="showBadge"
          />
        </span>
        <span v-if="showBadge" class="notification-dot"></span>
      </div>
    </div>
    <div class="dropdown-menu" role="menu">
      <NotificationDropdown />
    </div>
  </div>
</template>

<style scoped lang="scss">
.notification-bell-dropdown {
  .dropdown-menu {
    padding-top: 0.5rem;
    min-width: auto;
  }

  .dropdown-content {
    padding: 0;
    border-radius: 8px;
    overflow: hidden;
  }
}

.navbar-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0.25rem;
  width: 24px;
  height: 24px;
  box-sizing: border-box;
}

.navbar-icon {
  font-size: 1.25rem;
  color: #7a7a7a;
  transition: color 0.2s ease;

  &:hover {
    color: var(--bulma-primary, #00d1b2);
  }
}

.notification-dot {
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 8px;
  background-color: var(--bulma-danger);
  border-radius: 50%;
  border: 2px solid var(--bulma-white);
  z-index: 10;
  animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--bulma-danger-rgb), 0.7);
  }
  70% {
    box-shadow: 0 0 0 4px rgba(var(--bulma-danger-rgb), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--bulma-danger-rgb), 0);
  }
}
</style>
